import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { FaEraser, FaLink, FaUnlink } from "react-icons/fa";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { doUpdateData } from "../../../../U3148Service";
import U2000Panel from "../../../../../u2000Core/components/layout/U2000Panel";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000DeleteRecordDialog from "../../../../../u2000Core/components/dialog/U2000DeleteRecordDialog";
import U2000InputTextBase from "../../../../../u2000Core/components/formControls/input/U2000InputTextBase";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS, U3148ROLES } from "../../../../U3148";
import { luHandleAppiError } from "../../../../U3148Service";
import type { U3201GoogleCalendarStatus } from "../../../../../u3201GoogleCalendar/U3201Service";
import { useU3201GoogleCalendarHandlers } from "../../../../../u3201GoogleCalendar/U3201UseGoogleCalendarHandlers";
import {
  buildU3148GoogleCalendarOAuthFrontendRedirectUrl,
  isGenericFrontendOriginUrl,
} from "../../../../../u3201GoogleCalendar/U3201GoogleCalendarOAuthReturn";
import type { U3148OrganizationFormExtendedHandlers } from "../../../U3148OrganizationForm";
import {
  getGoogleCalendarPersoValue,
  GOOGLE_CALENDAR_PERSO_CODES,
  removeGoogleCalendarPerso,
  setGoogleCalendarPersoValue,
} from "./U3148GoogleCalendarPersonalizationHelpers";

interface U3148OrganizationGoogleCalendarBodyProps {
  u3148OrganizationFormHandlers: U3148OrganizationFormExtendedHandlers;
  /** Incrémenté après save org réussie (onglet actif) pour rafraîchir le statut U3201. */
  statusRefreshKey?: number;
}

function FieldHint({ children }: { children: ReactNode }) {
  return <p className="mt-1 text-xs text-gray-500">{children}</p>;
}

function statusBadgeClass(connected: boolean): string {
  return connected ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700";
}

function GoogleCalendarStatusIndicators({
  status,
  t,
}: {
  status: U3201GoogleCalendarStatus | null;
  t: TFunction;
}) {
  if (!status) return null;

  return (
    <div className="flex flex-wrap gap-3 text-sm">
      <span className={`rounded-sm px-2 py-0.5 font-medium ${statusBadgeClass(status.connected)}`}>
        {status.connected ? t("lblGoogleCalendarConnected", { ns: NS }) : t("lblGoogleCalendarNotConnected", { ns: NS })}
      </span>
      {status.connected && status.calendarId && (
        <span className="text-gray-600">
          {t("lblGoogleCalendarActiveCalendarId", { ns: NS })}: <span className="font-mono">{status.calendarId}</span>
        </span>
      )}
    </div>
  );
}

function U3148OrganizationGoogleCalendarBody({
  u3148OrganizationFormHandlers,
  statusRefreshKey = 0,
}: U3148OrganizationGoogleCalendarBodyProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const [status, setStatus] = useState<U3201GoogleCalendarStatus | null>(null);
  const [disconnectDlgOpen, setDisconnectDlgOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [internalStatusRefreshKey, setInternalStatusRefreshKey] = useState(0);
  const [clientSecretDraft, setClientSecretDraft] = useState("");
  const [clientSecretTouched, setClientSecretTouched] = useState(false);
  const [resetSecretDlgOpen, setResetSecretDlgOpen] = useState(false);
  const [resetSecretLoading, setResetSecretLoading] = useState(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const org = u3148OrganizationFormHandlers.getData();
  const orgId = org?.id;
  const readonly = u3148OrganizationFormHandlers.getFormState?.() === U2000FormStateEnumState.READ;
  const canEdit = hasRole(U3148ROLES.EDIT);
  const oauthFieldsDisabled = readonly || !canEdit;

  const clientIdValue = getGoogleCalendarPersoValue(org?.personalizations, GOOGLE_CALENDAR_PERSO_CODES.clientId);
  const redirectUriValue = getGoogleCalendarPersoValue(org?.personalizations, GOOGLE_CALENDAR_PERSO_CODES.oauthRedirectUri);
  const frontendRedirectUrlValue = getGoogleCalendarPersoValue(org?.personalizations, GOOGLE_CALENDAR_PERSO_CODES.oauthFrontendRedirectUrl);
  const calendarIdValue =
    getGoogleCalendarPersoValue(org?.personalizations, GOOGLE_CALENDAR_PERSO_CODES.calendarId) || status?.calendarId || "primary";
  const calendarIdDeliverableValue = getGoogleCalendarPersoValue(
    org?.personalizations,
    GOOGLE_CALENDAR_PERSO_CODES.calendarIdDeliverable,
  );

  const secretPlaceholder =
    !clientSecretTouched && !clientSecretDraft && status?.oauthConfigured
      ? t("hintGoogleCalendarClientSecretConfigured", { ns: NS })
      : undefined;

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const handleStatusSuccess = useCallback((s: U3201GoogleCalendarStatus) => {
    setStatus(s);
    setStatusLoading(false);
  }, []);

  const handleStatusError = useCallback(
    (error: unknown) => {
      setStatusLoading(false);
      handleApiError(error);
    },
    [handleApiError],
  );

  const handleDisconnectSuccess = useCallback(() => {
    toast.success(t("msgGoogleCalendarDisconnected", { ns: NS }));
    setDisconnectDlgOpen(false);
    setInternalStatusRefreshKey((k) => k + 1);
  }, [t, toast]);

  const googleCalendarHandlers = useU3201GoogleCalendarHandlers({
    orgId,
    oauthReturnLogicalUnit: "U3148",
    onStatusSuccess: handleStatusSuccess,
    onStatusError: handleStatusError,
    onDisconnectSuccess: handleDisconnectSuccess,
    onDisconnectError: handleApiError,
    onOAuthError: handleApiError,
  });

  useEffect(() => {
    if (!orgId) return;
    setStatusLoading(true);
    void googleCalendarHandlers.fetchStatus();
  }, [orgId, statusRefreshKey, internalStatusRefreshKey, googleCalendarHandlers.fetchStatus]);

  useEffect(() => {
    setClientSecretDraft("");
    setClientSecretTouched(false);
  }, [orgId, statusRefreshKey]);

  useEffect(() => {
    if (!orgId || readonly || !canEdit) return;
    const currentOrg = u3148OrganizationFormHandlers.getData();
    if (!currentOrg) return;
    const expected = buildU3148GoogleCalendarOAuthFrontendRedirectUrl(orgId);
    if (frontendRedirectUrlValue === expected) return;
    if (isGenericFrontendOriginUrl(frontendRedirectUrlValue)) {
      u3148OrganizationFormHandlers.setData(
        setGoogleCalendarPersoValue(currentOrg, GOOGLE_CALENDAR_PERSO_CODES.oauthFrontendRedirectUrl, expected, "user"),
      );
    }
  }, [orgId, frontendRedirectUrlValue, readonly, canEdit, u3148OrganizationFormHandlers]);

  const updatePerso = (code: string, value: string, scope: "admin" | "user") => {
    if (!org) return;
    u3148OrganizationFormHandlers.setData(setGoogleCalendarPersoValue(org, code, value, scope));
  };

  const handleClientSecretChange = (value: string) => {
    if (!org) return;
    const nextValue = String(value);
    setClientSecretDraft(nextValue);
    setClientSecretTouched(true);
    if (nextValue.trim()) {
      u3148OrganizationFormHandlers.setData(
        setGoogleCalendarPersoValue(org, GOOGLE_CALENDAR_PERSO_CODES.clientSecret, nextValue, "user"),
      );
    } else if (!status?.oauthConfigured) {
      u3148OrganizationFormHandlers.setData(removeGoogleCalendarPerso(org, GOOGLE_CALENDAR_PERSO_CODES.clientSecret));
    }
  };

  const handleResetSecretConfirm = async (result: "yes" | "no") => {
    if (result !== "yes") {
      setResetSecretDlgOpen(false);
      return;
    }
    const currentOrg = u3148OrganizationFormHandlers.getData();
    if (!currentOrg?.id) {
      setResetSecretDlgOpen(false);
      return;
    }
    setResetSecretLoading(true);
    try {
      await doUpdateData(u2000AxiosAuthenticated, currentOrg.id, currentOrg, {
        resetOrgGoogleCalendarClientSecret: true,
      });
      toast.success(t("msgGoogleCalendarClientSecretResetSuccess", { ns: NS }));
      setClientSecretDraft("");
      setClientSecretTouched(false);
      setResetSecretDlgOpen(false);
      setInternalStatusRefreshKey((k) => k + 1);
    } catch (error) {
      handleApiError(error);
    } finally {
      setResetSecretLoading(false);
    }
  };

  const handleDisconnectConfirm = (result: "yes" | "no") => {
    if (result === "yes") {
      void googleCalendarHandlers.disconnect();
    } else {
      setDisconnectDlgOpen(false);
    }
  };

  if (!orgId) {
    return <p className="text-sm text-gray-500">{t("msgGoogleCalendarSaveOrgFirst", { ns: NS })}</p>;
  }

  return (
    <div className="space-y-8">
      <U2000Panel title={t("lblGoogleCalendarOAuthConfig", { ns: NS })}>
        {status?.oauthConfigured === false && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t("msgGoogleCalendarOAuthOrgNotConfigured", { ns: NS })}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <U2000InputTextBase
              fieldName="googleCalendarClientId"
              label={t("lblGoogleCalendarClientId", { ns: NS })}
              type="string"
              value={clientIdValue}
              setValue={(value) => updatePerso(GOOGLE_CALENDAR_PERSO_CODES.clientId, String(value), "user")}
              disabled={oauthFieldsDisabled}
            />
            <FieldHint>{t("hintGoogleCalendarClientId", { ns: NS })}</FieldHint>
          </div>

          <div>
            <U2000InputTextBase
              fieldName="googleCalendarClientSecret"
              label={t("lblGoogleCalendarClientSecret", { ns: NS })}
              type="password"
              value={clientSecretDraft}
              setValue={(value) => handleClientSecretChange(String(value))}
              disabled={oauthFieldsDisabled}
              placeholder={secretPlaceholder}
            />
            <FieldHint>{t("hintGoogleCalendarClientSecret", { ns: NS })}</FieldHint>
            {canEdit && !readonly && status?.oauthConfigured === true && (
              <div className="mt-2">
                <U2000ActionBtn
                  label={t("btnGoogleCalendarResetClientSecret", { ns: NS })}
                  icon={FaEraser}
                  handleClick={() => setResetSecretDlgOpen(true)}
                  disabled={resetSecretLoading}
                />
              </div>
            )}
          </div>

          <div>
            <U2000InputTextBase
              fieldName="googleCalendarOauthRedirectUri"
              label={t("lblGoogleCalendarOauthRedirectUri", { ns: NS })}
              type="string"
              value={redirectUriValue}
              setValue={(value) => updatePerso(GOOGLE_CALENDAR_PERSO_CODES.oauthRedirectUri, String(value), "user")}
              disabled={oauthFieldsDisabled}
              placeholder="http://localhost:8081/U3201/public/oauth/callback"
            />
            <FieldHint>{t("hintGoogleCalendarOauthRedirectUri", { ns: NS })}</FieldHint>
          </div>

          <div>
            <U2000InputTextBase
              fieldName="googleCalendarOauthFrontendRedirectUrl"
              label={t("lblGoogleCalendarOauthFrontendRedirectUrl", { ns: NS })}
              type="string"
              value={frontendRedirectUrlValue}
              setValue={(value) => updatePerso(GOOGLE_CALENDAR_PERSO_CODES.oauthFrontendRedirectUrl, String(value), "user")}
              disabled={oauthFieldsDisabled}
              placeholder={orgId ? buildU3148GoogleCalendarOAuthFrontendRedirectUrl(orgId) : "http://localhost:3000/U3148/{orgId}"}
            />
            <FieldHint>{t("hintGoogleCalendarOauthFrontendRedirectUrl", { ns: NS })}</FieldHint>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">{t("msgGoogleCalendarSaveOrgToPersist", { ns: NS })}</p>
      </U2000Panel>

      <U2000Panel title={t("lblGoogleCalendarConnection", { ns: NS })}>
        <div className="space-y-4">
          {statusLoading && !status ? (
            <p className="text-sm text-gray-500">{t("msgGoogleCalendarLoadingStatus", { ns: NS })}</p>
          ) : (
            <GoogleCalendarStatusIndicators status={status} t={t} />
          )}

          {canEdit && !readonly && (
            <div className="flex flex-wrap gap-2">
              {status?.oauthConfigured === true && !status.connected && (
                <U2000ActionBtn label={t("btnGoogleCalendarConnect", { ns: NS })} icon={FaLink} handleClick={() => googleCalendarHandlers.startOAuth()} />
              )}
              {status?.connected && (
                <U2000ActionBtn label={t("btnGoogleCalendarDisconnect", { ns: NS })} icon={FaUnlink} handleClick={() => setDisconnectDlgOpen(true)} />
              )}
            </div>
          )}
        </div>
      </U2000Panel>

      <U2000Panel title={t("lblGoogleCalendarSettings", { ns: NS })}>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <U2000InputTextBase
              fieldName="googleCalendarCalendarId"
              label={t("lblGoogleCalendarCalendarId", { ns: NS })}
              type="string"
              value={calendarIdValue}
              setValue={(value) => updatePerso(GOOGLE_CALENDAR_PERSO_CODES.calendarId, String(value), "user")}
              disabled={oauthFieldsDisabled}
              placeholder="primary"
            />
            <FieldHint>{t("hintGoogleCalendarCalendarId", { ns: NS })}</FieldHint>
          </div>

          <div>
            <U2000InputTextBase
              fieldName="googleCalendarCalendarIdDeliverable"
              label={t("lblGoogleCalendarCalendarIdDeliverable", { ns: NS })}
              type="string"
              value={calendarIdDeliverableValue}
              setValue={(value) => updatePerso(GOOGLE_CALENDAR_PERSO_CODES.calendarIdDeliverable, String(value), "user")}
              disabled={oauthFieldsDisabled}
            />
            <FieldHint>{t("hintGoogleCalendarCalendarIdDeliverable", { ns: NS })}</FieldHint>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">{t("msgGoogleCalendarSaveOrgToPersist", { ns: NS })}</p>
      </U2000Panel>

      <U2000DeleteRecordDialog isOpen={disconnectDlgOpen} onClose={handleDisconnectConfirm} title={t("lblGoogleCalendarDisconnectDlgTitle", { ns: NS })}>
        <p>{t("msgGoogleCalendarDisconnectConfirm", { ns: NS })}</p>
      </U2000DeleteRecordDialog>

      <U2000DeleteRecordDialog
        isOpen={resetSecretDlgOpen}
        onClose={handleResetSecretConfirm}
        title={t("lblGoogleCalendarResetClientSecretDlgTitle", { ns: NS })}
      >
        <p>{t("msgGoogleCalendarResetClientSecretConfirm", { ns: NS })}</p>
      </U2000DeleteRecordDialog>
    </div>
  );
}

export default U3148OrganizationGoogleCalendarBody;
